import { useState, useEffect, useCallback, useRef } from "react";
import { MOCK } from "./MockData";
import { fmt } from "./Utils/Utilities";
import { Badge, ActionMenu, Modal } from "./SharedUI";

const SITES = ["All Sites", "Sky Hill", "Moonstone", "Sasol"];

function InventoryPage({ showToast: toast }) {
  const [inventory, setInventory] = useState(MOCK.inventory);
  const [selectedSite, setSelectedSite] = useState("All Sites");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({
    product: "",
    site: "Sky Hill",
    unit: "kg",
    stock: "",
    minStock: "",
    maxStock: "",
    supplier: "",
  });

  const handleCreate = () => {
    if (!form.product || !form.site)
      return toast("Product name and site required", "e");
    const newItem = {
      ...form,
      id: `INV-${String(inventory.length + 1).padStart(3, "0")}`,
      stock: Number(form.stock),
      minStock: Number(form.minStock),
      maxStock: Number(form.maxStock),
      status: Number(form.stock) < Number(form.minStock) ? "Low" : "Sufficient",
    };
    setInventory((p) => [...p, newItem]);
    toast("Inventory item added", "s");
    // API: API.inventory.create(form)
    setModal(null);
  };

  const handleRefill = (item) => {
    setInventory((p) =>
      p.map((i) =>
        i.id === item.id
          ? {
              ...i,
              stock: i.maxStock,
              status: "Sufficient",
            }
          : i,
      ),
    );
    toast(`${item.product} refilled to max capacity`, "s");
    // API: API.inventory.update(item.id, { stock: item.maxStock })
  };

  const filteredInventory = inventory.filter(
    (i) => selectedSite === "All Sites" || i.site === selectedSite,
  );

  const alertItems = filteredInventory.filter(
    (i) => i.status === "Critical" || i.status === "Low",
  );

  const totalCylinders = filteredInventory.reduce((sum, i) => {
    const isCylinder = i.unit === "units" || /cyl/i.test(i.product);
    return sum + (isCylinder ? Number(i.stock) : 0);
  }, 0);

  return (
    <div className="page">
      {alertItems.length > 0 && (
        <div
          style={{
            background: "rgba(255,23,68,.07)",
            border: "1px solid rgba(255,23,68,.25)",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <i className="bi bi-exclamation-triangle" style={{ fontSize: 16 }} />
          <span className="fs12" style={{ color: "var(--danger)" }}>
            <strong>{alertItems.length}</strong> items below minimum stock
            threshold require attention.
          </span>
          <span className="fs11 tc3" style={{ marginLeft: "auto" }}>
            Critical: {alertItems.filter((i) => i.status === "Critical").length}{" "}
            · Low: {alertItems.filter((i) => i.status === "Low").length}
          </span>
        </div>
      )}

      <div className="panel">
        <div className="tbar">
          <span className="ph-title">Stock Management</span>

          <select
            className="fsel"
            style={{ marginLeft: 12, width: 160 }}
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
          >
            {SITES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <div style={{ marginLeft: 12, alignSelf: "center" }} className="fs11 tc3">
            Cylinders: <strong className="mono">{fmt.number(totalCylinders)}</strong>
          </div>

          <button
            className="btn btn-p"
            style={{ marginLeft: "auto" }}
            onClick={() => setModal("create")}
          >
            <i className="bi bi-plus-lg" /> Add Item
          </button>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Site</th>
              <th>Product</th>
              <th>Current Stock</th>
              <th>Min / Max</th>
              <th>Capacity</th>
              <th>Supplier</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.map((item) => {
              const pct = Math.round((item.stock / item.maxStock) * 100);
              const fill =
                item.status === "Critical"
                  ? "var(--danger)"
                  : item.status === "Low"
                    ? "var(--warn)"
                    : "var(--teal)";
              return (
                <tr key={item.id}>
                  <td className="mono tc2 fs10">{item.site}</td>
                  <td className="fs12 fw6">{item.product}</td>
                  <td className="mono fw7">
                    {fmt.number(item.stock)}{" "}
                    <span className="tc3 fs10">{item.unit}</span>
                    {(() => {
                      const isCylinder = item.unit === "units" || /cyl/i.test(item.product) || /cylinder/i.test(item.product) || /cylinders/i.test(item.unit);
                      if (!isCylinder) return null;
                      const breakdown = SITES.filter(s => s !== "All Sites").map((s) => {
                        const qty = inventory.filter(i => i.product === item.product && i.site === s).reduce((sum, it) => sum + (Number(it.stock) || 0), 0);
                        return { site: s, qty };
                      }).filter(b => b.qty > 0);
                      if (breakdown.length === 0) return null;
                      return (
                        <div className="fs10 tc3" style={{ marginTop: 6 }}>

                        </div>
                      );
                    })()}
                  </td>
                  <td className="mono tc3 fs10">
                    {fmt.number(item.minStock)} / {fmt.number(item.maxStock)}
                  </td>
                  <td style={{ minWidth: 100 }}>
                    <div className="fc gap8">
                      <div className="prog-wrap" style={{ flex: 1 }}>
                        <div
                          className="prog-fill"
                          style={{ width: `${pct}%`, background: fill }}
                        />
                      </div>
                      <span className="mono fs10 tc3">{pct}%</span>
                    </div>
                  </td>

                  <td className="fs11 tc2">{item.supplier}</td>
                  <td>
                    <Badge status={item.status} />
                  </td>
                  <td>
                    <ActionMenu
                      items={[
                        {
                          icon: <i className="bi bi-arrow-repeat" />,
                          label: "Refill to Max",
                          action: () => handleRefill(item),
                        },
                        {
                          icon: <i className="bi bi-pencil" />,
                          label: "Edit Item",
                          action: () => toast(`Edit ${item.product}`, "s"),
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal === "create" && (
        <Modal
          title="Add Inventory Item"
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-g" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button className="btn btn-p" onClick={handleCreate}>
                Add Item
              </button>
            </>
          }
        >
            <div className="frow">
              <div className="fg">
                <label className="fl">Product Name *</label>
                <input
                  className="fi"
                  value={form.product}
                  onChange={(e) => setForm({ ...form, product: e.target.value })}
                  placeholder="e.g. LPG Bulk"
                />
              </div>
              <div className="fg">
                <label className="fl">Site</label>
                <select className="fsel" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })}>
                  {SITES.filter(s => s !== 'All Sites').map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          <div className="frow3">
            <div className="fg">
              <label className="fl">Current Stock</label>
              <input
                className="fi"
                type="number"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="fg">
              <label className="fl">Min Stock</label>
              <input
                className="fi"
                type="number"
                value={form.minStock}
                onChange={(e) => setForm({ ...form, minStock: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="fg">
              <label className="fl">Max Stock</label>
              <input
                className="fi"
                type="number"
                value={form.maxStock}
                onChange={(e) => setForm({ ...form, maxStock: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
          <div className="frow">
            <div className="fg">
              <label className="fl">Unit</label>
              <select
                className="fsel"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              >
                {["kg",  "Cylinders"].map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </select>
            </div>
       
          </div>
          <div className="fg">
            <label className="fl">Supplier</label>
            <input
              className="fi"
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              placeholder="Supplier company name"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

export default InventoryPage;
